import classNames from 'classnames'
import {type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ElementType} from 'react'
import styles from './button.module.scss'

export type ButtonProps = (
    | (ButtonHTMLAttributes<HTMLButtonElement> & { as?: 'button' })
    | (AnchorHTMLAttributes<HTMLAnchorElement> & {
          as: 'link'
          /**
           * Element used to render the link — defaults to a native <a> (uses `href`).
           * Inject a router Link at the call site (e.g. Remix's `Link`) for client-side
           * navigation and pass its target via `to`. Keeps this primitive router-agnostic
           * so it bundles without a framework dependency.
           */
          linkComponent?: ElementType
          to?: string | { pathname?: string; search?: string; hash?: string }
      })
    ) & {
    variant?: 'primary' | 'secondary' | 'light' | 'link' | 'text' | 'ghost'
    size?: 'small' | 'medium' | 'large'
    isLoading?: boolean
}

export function Button({
                           variant = 'primary',
                           size = 'medium',
                           isLoading = false,
                           className,
                           children,
                           ...props
                       }: ButtonProps) {
    const buttonClassName = classNames(
        styles.button,
        {
            [styles.buttonPrimary]: variant === 'primary',
            [styles.buttonSecondary]: variant === 'secondary',
            [styles.buttonLight]: variant === 'light',
            [styles.buttonLink]: variant === 'link',
            [styles.buttonText]: variant === 'text',
            [styles.buttonGhost]: variant === 'ghost',
            [styles.buttonSmall]: size === 'small',
            [styles.buttonMedium]: size === 'medium',
            [styles.buttonLarge]: size === 'large',
            [styles.buttonLoading]: isLoading,
        },
        className,
    )

    if (props.as === 'link') {
        const {linkComponent: LinkComponent = 'a', ...linkProps} = props
        return (
            <LinkComponent className={buttonClassName} {...linkProps}>
                <div className={styles.buttonContent}>{children}</div>
            </LinkComponent>
        )
    }

    return (
        <button className={buttonClassName} {...props}>
            <div className={styles.buttonContent}>
                {children}
            </div>
        </button>
    )
}
